import { useCallback, useRef } from 'react';
import { MutationTuple, useMutation } from '@apollo/client';
import { v4 as uuidv4 } from 'uuid';
// Api
import {
  SEND_SMS_OTP,
  VERIFY_SMS_OTP,
  SEND_EMAIL_OTP,
  VERIFY_EMAIL_OTP,
} from 'api/otp/mutations';
import { SendSmsOtp, SendSmsOtpVariables } from 'api/otp/types/SendSmsOtp';
import {
  SendEmailOtp,
  SendEmailOtpVariables,
} from 'api/otp/types/SendEmailOtp';
import {
  VerifySmsOtp,
  VerifySmsOtpVariables,
} from 'api/otp/types/VerifySmsOtp';
import {
  VerifyEmailOtp,
  VerifyEmailOtpVariables,
} from 'api/otp/types/VerifyEmailOtp';
// Components
import { showToast } from 'components/common/Toast/Toast';

type VerificationType = 'phone' | 'email';
type VerificationResponse = {
  isValid: boolean;
  verificationId?: string;
};

type SendOtpMutation = MutationTuple<
  SendSmsOtp | SendEmailOtp,
  SendSmsOtpVariables | SendEmailOtpVariables
>;
type VerifyOtpMutation = MutationTuple<
  VerifySmsOtp | VerifyEmailOtp,
  VerifySmsOtpVariables | VerifyEmailOtpVariables
>;

const handleSendOtp = async (
  type: VerificationType,
  sendOtp: SendOtpMutation[0],
  contact: string
): Promise<VerificationResponse> => {
  try {
    const newReferenceId = uuidv4();
    await sendOtp({
      variables:
        type === 'phone'
          ? { input: { phoneNumber: contact, referenceId: newReferenceId } }
          : { input: { email: contact } },
    });
    return { isValid: true, verificationId: newReferenceId };
  } catch (error) {
    showToast({ message: 'Failed to send verification code', type: 'error' });
    return { isValid: false };
  }
};

const handleVerifyOtp = async (
  type: VerificationType,
  verifyOtp: VerifyOtpMutation[0],
  contact: string,
  code: string,
  referenceId?: string
): Promise<VerificationResponse> => {
  try {
    const { data } = await verifyOtp({
      variables:
        type === 'phone'
          ? {
              input: {
                code,
                phoneNumber: contact,
                referenceId: referenceId || '',
              },
            }
          : { input: { code, email: contact } },
    });

    if (!data) {
      showToast({ message: 'Invalid verification code', type: 'error' });
      return { isValid: false };
    }

    const response =
      type === 'phone'
        ? (data as VerifySmsOtp)?.verifyOtp
        : (data as VerifyEmailOtp)?.verifyEmailOtp;

    if (!response?.isValid) {
      showToast({ message: 'Invalid verification code', type: 'error' });
      return { isValid: false };
    }

    /**
     * This will pass, but on sign up, validation can fail
     * so commenting this to not get two toasts, one with the error and one with the success
     */
    // showToast({ message: 'Validated successfully' });
    return {
      isValid: true,
      verificationId:
        'verificationId' in response ? response.verificationId : undefined,
    };
  } catch (error) {
    showToast({ message: 'Failed to verify code', type: 'error' });
    return { isValid: false };
  }
};

const handleOtpVerification = async (
  type: VerificationType,
  sendOtp: SendOtpMutation[0],
  verifyOtp: VerifyOtpMutation[0],
  contact: string,
  code?: string,
  referenceId?: string
): Promise<VerificationResponse> => {
  if (!code) {
    return handleSendOtp(type, sendOtp, contact);
  }
  return handleVerifyOtp(type, verifyOtp, contact, code, referenceId);
};

export const useSmsOtpVerification = (phoneNumber: string) => {
  const [sendSmsOtp] = useMutation(SEND_SMS_OTP);
  const [verifySmsOtp] = useMutation(VERIFY_SMS_OTP);
  const referenceIdRef = useRef<string>('');

  const sendVerification = useCallback(async () => {
    const result = await handleOtpVerification(
      'phone',
      sendSmsOtp,
      verifySmsOtp,
      phoneNumber
    );
    if (result.isValid) {
      referenceIdRef.current = result.verificationId || '';
    }
  }, [phoneNumber, sendSmsOtp, verifySmsOtp]);

  const verifyCode = useCallback(
    async (code: string) => {
      return handleOtpVerification(
        'phone',
        sendSmsOtp,
        verifySmsOtp,
        phoneNumber,
        code,
        referenceIdRef.current
      );
    },
    [phoneNumber, sendSmsOtp, verifySmsOtp]
  );

  return {
    sendVerification,
    verifyCode,
    hasReferenceId: !!referenceIdRef.current,
  };
};

export const useEmailOtpVerification = (email: string) => {
  const [sendEmailOtp] = useMutation(SEND_EMAIL_OTP);
  const [verifyEmailOtp] = useMutation(VERIFY_EMAIL_OTP);
  const referenceIdRef = useRef<string>('');

  const sendVerification = useCallback(
    async (overrideEmail?: string) => {
      const emailToUse = overrideEmail || email;
      const result = await handleOtpVerification(
        'email',
        sendEmailOtp,
        verifyEmailOtp,
        emailToUse
      );
      if (result.isValid) {
        referenceIdRef.current = result.verificationId || '';
      }
    },
    [email, sendEmailOtp, verifyEmailOtp]
  );

  const verifyCode = useCallback(
    async (code: string, overrideEmail?: string) => {
      const emailToUse = overrideEmail || email;
      return handleOtpVerification(
        'email',
        sendEmailOtp,
        verifyEmailOtp,
        emailToUse,
        code,
        referenceIdRef.current
      );
    },
    [email, sendEmailOtp, verifyEmailOtp]
  );

  return {
    sendVerification,
    verifyCode,
    hasReferenceId: !!referenceIdRef.current,
  };
};
